import { createFileRoute, useNavigate, useParams } from '@tanstack/react-router'
import { useState } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { payloadAPI } from '@/lib/payload'
import { useAlgorithmSEOMeta } from '@/hooks/use-seo-meta'
import { Button } from '@/components/ui/button'
import { formatCurrency, formatPercentage, formatNumber } from '@/lib/utils'
import { BacktestModal } from '@/components/backtest/backtest-modal'
import { PerformanceChart } from '@/components/charts/performance-chart'
import { AlgorithmFiles } from '@/components/algorithm/algorithm-files'
import { LoadingSpinner } from '@/components/ui/loading-spinner'
import { StrategicCTA, InlineCTABanner } from '@/components/ui/strategic-cta'
import {
  Star,
  Download,
  Play,
  BarChart3,
  Users,
  Calendar,
  TrendingUp,
  TrendingDown,
  Shield,
  Zap,
  Settings,
  Heart,
  Share2,
  MessageSquare,
  BookOpen,
} from 'lucide-react'
import { toast } from 'sonner'

export const Route = createFileRoute('/algorithm/$slug')({
  component: AlgorithmDetailComponent,
})

function AlgorithmDetailComponent() {
  const { slug } = useParams({ from: '/algorithm/$slug' })
  const navigate = useNavigate()
  const [showBacktestModal, setShowBacktestModal] = useState(false)
  const [activeTab, setActiveTab] = useState<'overview' | 'performance' | 'backtests' | 'reviews' | 'files'>('overview')

  const { data: algorithm, isLoading, error } = useQuery({
    queryKey: ['algorithm', slug],
    queryFn: () => payloadAPI.getAlgorithmBySlug(slug),
  })

  // Apply SEO meta tags once algorithm data is loaded
  if (algorithm) {
    useAlgorithmSEOMeta(algorithm)
  }

  const { data: reviews } = useQuery({
    queryKey: ['algorithm-reviews', algorithm?.id],
    queryFn: () => algorithm ? payloadAPI.getAlgorithmReviews(algorithm.id) : null,
    enabled: !!algorithm,
  })

  const { data: backtests } = useQuery({
    queryKey: ['algorithm-backtests', algorithm?.id],
    queryFn: () => algorithm ? payloadAPI.getAlgorithmBacktests(algorithm.id) : null,
    enabled: !!algorithm,
  })

  const downloadMutation = useMutation({
    mutationFn: () => payloadAPI.downloadAlgorithm(algorithm!.id),
    onSuccess: () => {
      toast.success('Algorithm download started')
    },
    onError: () => {
      toast.error('Failed to download algorithm')
    }
  })

  const favoriteMutation = useMutation({
    mutationFn: () => payloadAPI.toggleFavoriteAlgorithm(algorithm!.id),
    onSuccess: () => {
      toast.success('Added to favorites')
    },
    onError: () => {
      toast.error('Failed to add to favorites')
    }
  })

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error || !algorithm) {
    return (
      <div className="text-center py-12">
        <p className="text-destructive">Algorithm not found</p>
        <Button
          variant="outline"
          className="mt-4"
          onClick={() => navigate({ to: '/marketplace' })}
        >
          Back to Marketplace
        </Button>
      </div>
    )
  }

  const isFree = algorithm.pricingType === 'free'
  const price = algorithm.pricingType === 'one-time' ? algorithm.price :
    algorithm.pricingType === 'subscription' ? algorithm.subscriptionPrice?.monthly : 0

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`h-4 w-4 ${i < Math.floor(rating) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
      />
    ))
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate({ to: '/marketplace' })}
            className="mb-4"
          >
            ← Back to Marketplace
          </Button>

          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-8">
            <div className="flex-1">
              <div className="flex items-center gap-4 mb-4">
                <h1 className="text-3xl md:text-4xl font-bold text-foreground">
                  {algorithm.name}
                </h1>
                {algorithm.featured && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                    Featured
                  </span>
                )}
              </div>

              <p className="text-lg text-muted-foreground mb-6">
                {algorithm.description}
              </p>

              <div className="flex items-center gap-6 mb-6">
                <div className="flex items-center gap-1">
                  {renderStars(algorithm.rating)}
                  <span className="text-sm text-muted-foreground ml-2">
                    {algorithm.rating.toFixed(1)} ({algorithm.reviewsCount} reviews)
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Users className="h-4 w-4" />
                  {formatNumber(algorithm.downloads)} downloads
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Calendar className="h-4 w-4" />
                  Updated {new Date(algorithm.updatedAt).toLocaleDateString()}
                </div>
              </div>

              {/* Performance Metrics */}
              {algorithm.performance && (
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
                  <div className="bg-card rounded-lg p-4 border">
                    <div className="flex items-center gap-2 mb-1">
                      <TrendingUp className="h-4 w-4 text-green-500" />
                      <span className="text-xs text-muted-foreground">Total Return</span>
                    </div>
                    <div className="text-lg font-semibold text-green-500">
                      {formatPercentage(algorithm.performance.totalReturn)}
                    </div>
                  </div>

                  <div className="bg-card rounded-lg p-4 border">
                    <div className="flex items-center gap-2 mb-1">
                      <Shield className="h-4 w-4 text-blue-500" />
                      <span className="text-xs text-muted-foreground">Sharpe Ratio</span>
                    </div>
                    <div className="text-lg font-semibold text-blue-500">
                      {algorithm.performance.sharpeRatio?.toFixed(2) || 'N/A'}
                    </div>
                  </div>

                  <div className="bg-card rounded-lg p-4 border">
                    <div className="flex items-center gap-2 mb-1">
                      <BarChart3 className="h-4 w-4 text-purple-500" />
                      <span className="text-xs text-muted-foreground">Win Rate</span>
                    </div>
                    <div className="text-lg font-semibold text-purple-500">
                      {formatPercentage(algorithm.performance.winRate)}
                    </div>
                  </div>

                  <div className="bg-card rounded-lg p-4 border">
                    <div className="flex items-center gap-2 mb-1">
                      <TrendingDown className="h-4 w-4 text-red-500" />
                      <span className="text-xs text-muted-foreground">Max Drawdown</span>
                    </div>
                    <div className="text-lg font-semibold text-red-500">
                      {formatPercentage(algorithm.performance.maxDrawdown)}
                    </div>
                  </div>

                  <div className="bg-card rounded-lg p-4 border">
                    <div className="flex items-center gap-2 mb-1">
                      <Zap className="h-4 w-4 text-orange-500" />
                      <span className="text-xs text-muted-foreground">Profit Factor</span>
                    </div>
                    <div className="text-lg font-semibold text-orange-500">
                      {algorithm.performance.profitFactor?.toFixed(2) || 'N/A'}
                    </div>
                  </div>

                  <div className="bg-card rounded-lg p-4 border">
                    <div className="flex items-center gap-2 mb-1">
                      <Settings className="h-4 w-4 text-gray-500" />
                      <span className="text-xs text-muted-foreground">Min Capital</span>
                    </div>
                    <div className="text-lg font-semibold text-gray-500">
                      {formatCurrency(algorithm.requirements?.minCapital || 0)}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Pricing & Actions */}
            <div className="lg:w-80">
              <div className="bg-card rounded-lg p-6 border">
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-muted-foreground">Price</span>
                    {isFree && (
                      <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                        FREE
                      </span>
                    )}
                  </div>
                  <div className="text-3xl font-bold text-foreground">
                    {isFree ? 'Free' : formatCurrency(price)}
                    {!isFree && algorithm.pricingType === 'subscription' && (
                      <span className="text-lg font-normal text-muted-foreground">/month</span>
                    )}
                  </div>
                </div>

                <div className="space-y-3">
                  <Button
                    className="w-full"
                    onClick={() => setShowBacktestModal(true)}
                  >
                    <Play className="h-4 w-4 mr-2" />
                    Run Backtest
                  </Button>

                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => downloadMutation.mutate()}
                    disabled={downloadMutation.isPending}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    {downloadMutation.isPending ? 'Downloading...' : 'Download'}
                  </Button>

                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={() => favoriteMutation.mutate()}
                      disabled={favoriteMutation.isPending}
                    >
                      <Heart className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" className="flex-1">
                      <Share2 className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" className="flex-1">
                      <MessageSquare className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {/* Algorithm Info */}
                <div className="mt-6 pt-6 border-t space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Category</span>
                    <span className="font-medium">{algorithm.category?.name}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Developer</span>
                    <span className="font-medium">{algorithm.developer?.name}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Platform</span>
                    <span className="font-medium capitalize">{algorithm.requirements?.platform}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Prop Firm</span>
                    <span className="font-medium">
                      {algorithm.requirements?.propFirmCompatible ? 'Compatible' : 'Not Compatible'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="mb-8">
          <div className="border-b border-border">
            <nav className="-mb-px flex space-x-8">
              {['overview', 'performance', 'backtests', 'reviews', 'files'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab as any)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm capitalize ${activeTab === tab
                      ? 'border-primary text-primary'
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                    }`}
                >
                  {tab}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        <div>
          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2 space-y-8">
                {/* Description */}
                <div>
                  <h2 className="text-2xl font-semibold mb-4">About this Algorithm</h2>
                  <div className="prose prose-gray max-w-none">
                    <p>{algorithm.fullDescription || algorithm.description}</p>
                  </div>
                </div>

                {/* Strategic CTA: Newsletter/Lead Capture */}
                <div>
                  <InlineCTABanner
                    message="Get more algorithms like this delivered to your inbox weekly"
                    ctaText="Join Newsletter"
                    variant="info"
                    onClick={() => console.log('Newsletter signup')}
                  />
                </div>

                {/* Trading Parameters */}
                <div>
                  <h3 className="text-xl font-semibold mb-4">Trading Parameters</h3>
                  <div className="bg-card rounded-lg p-6 border">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <span className="text-sm text-muted-foreground">Timeframe</span>
                        <div className="font-medium capitalize">{algorithm.tradingParameters?.timeframe}</div>
                      </div>
                      <div>
                        <span className="text-sm text-muted-foreground">Risk per Trade</span>
                        <div className="font-medium">{formatPercentage(algorithm.tradingParameters?.riskPerTrade)}</div>
                      </div>
                      <div>
                        <span className="text-sm text-muted-foreground">Max Positions</span>
                        <div className="font-medium">{algorithm.tradingParameters?.maxPositions}</div>
                      </div>
                      <div>
                        <span className="text-sm text-muted-foreground">Supported Assets</span>
                        <div className="font-medium capitalize">
                          {algorithm.tradingParameters?.supportedAssets?.join(', ')}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Strategic CTA: Consultation */}
                <div>
                  <StrategicCTA
                    variant="consultation"
                    size="default"
                    title="Need Help Implementing This Algorithm?"
                    description="Our experts can help you integrate this algorithm into your trading system"
                    primaryButtonText="Book Free Consultation"
                    onPrimaryClick={() => console.log('Consultation booking')}
                    secondaryButtonText="View Implementation Guide"
                    onSecondaryClick={() => console.log('View guide')}
                    features={[
                      '1-on-1 expert guidance',
                      'Custom configuration help',
                      'Performance optimization',
                      'Risk management setup'
                    ]}
                    socialProof="Join 500+ traders who've improved their results"
                  />
                </div>
              </div>

              {/* Sidebar */}
              <div className="space-y-8">
                {/* Developer Info */}
                <div>
                  <h3 className="text-xl font-semibold mb-4">Developer</h3>
                  <div className="bg-card rounded-lg p-6 border">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center text-primary-foreground font-semibold">
                        {algorithm.developer?.name?.charAt(0)}
                      </div>
                      <div>
                        <div className="font-medium">{algorithm.developer?.name}</div>
                        {algorithm.developer?.verified && (
                          <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                            Verified
                          </span>
                        )}
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground mb-4">
                      {algorithm.developer?.bio}
                    </p>
                    <Button variant="outline" className="w-full">
                      View Profile
                    </Button>
                  </div>
                </div>

                {/* Strategic CTA: Learning Resources */}
                <div>
                  <StrategicCTA
                    variant="default"
                    size="compact"
                    title="Learn Algorithmic Trading"
                    description="Master the fundamentals with our comprehensive courses"
                    primaryButtonText="Explore Courses"
                    onPrimaryClick={() => console.log('Explore courses')}
                    features={[
                      'Expert-led video lessons',
                      'Hands-on projects',
                      'Certificate of completion',
                      'Community access'
                    ]}
                    icon={<BookOpen className="h-4 w-4 ml-2" />}
                  />
                </div>

                {/* AI Optimizations */}
                {algorithm.aiOptimizations && algorithm.aiOptimizations.length > 0 && (
                  <div>
                    <h3 className="text-xl font-semibold mb-4">AI Optimizations</h3>
                    <div className="bg-card rounded-lg p-6 border space-y-3">
                      {algorithm.aiOptimizations.map((optimization: any, index: number) => (
                        <div key={index} className="border-b last:border-b-0 pb-3 last:pb-0">
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-medium capitalize">{optimization.type}</span>
                            {optimization.expectedImprovement && (
                              <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                                {optimization.expectedImprovement}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground">{optimization.description}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'performance' && (
            <div>
              <h2 className="text-2xl font-semibold mb-6">Performance Analysis</h2>
              <div className="bg-card rounded-lg p-6 border">
                <PerformanceChart data={algorithm.performance} />
              </div>
            </div>
          )}

          {activeTab === 'backtests' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-semibold">Backtest Results</h2>
                <Button onClick={() => setShowBacktestModal(true)}>
                  <Play className="h-4 w-4 mr-2" />
                  New Backtest
                </Button>
              </div>

              {backtests && backtests.docs.length > 0 ? (
                <div className="space-y-4">
                  {backtests.docs.map((backtest: any) => (
                    <div key={backtest.id} className="bg-card rounded-lg p-6 border">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="font-semibold">{backtest.name}</h3>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${backtest.status === 'completed'
                            ? 'bg-green-100 text-green-800'
                            : backtest.status === 'running'
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-red-100 text-red-800'
                          }`}>
                          {backtest.status}
                        </span>
                      </div>

                      {backtest.results && (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div>
                            <span className="text-sm text-muted-foreground">Total Return</span>
                            <div className="font-semibold">{formatPercentage(backtest.results.totalReturn)}</div>
                          </div>
                          <div>
                            <span className="text-sm text-muted-foreground">Win Rate</span>
                            <div className="font-semibold">{formatPercentage(backtest.results.winRate)}</div>
                          </div>
                          <div>
                            <span className="text-sm text-muted-foreground">Sharpe Ratio</span>
                            <div className="font-semibold">{backtest.results.sharpeRatio?.toFixed(2)}</div>
                          </div>
                          <div>
                            <span className="text-sm text-muted-foreground">Max Drawdown</span>
                            <div className="font-semibold">{formatPercentage(backtest.results.maxDrawdown)}</div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <p className="text-muted-foreground mb-4">No backtests run yet</p>
                  <Button onClick={() => setShowBacktestModal(true)}>
                    Run First Backtest
                  </Button>
                </div>
              )}
            </div>
          )}

          {activeTab === 'reviews' && (
            <div>
              <h2 className="text-2xl font-semibold mb-6">User Reviews</h2>
              {reviews && reviews.docs.length > 0 ? (
                <div className="space-y-6">
                  {reviews.docs.map((review: any) => (
                    <div key={review.id} className="bg-card rounded-lg p-6 border">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                            {review.user?.name?.charAt(0)}
                          </div>
                          <div>
                            <div className="font-medium">{review.user?.name}</div>
                            <div className="flex items-center gap-1">
                              {renderStars(review.rating)}
                              <span className="text-xs text-muted-foreground ml-1">
                                {new Date(review.createdAt).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <p className="text-muted-foreground">{review.comment}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <p className="text-muted-foreground">No reviews yet</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'files' && (
            <AlgorithmFiles algorithm={algorithm} />
          )}
        </div>
      </div>

      {/* Backtest Modal */}
      {showBacktestModal && (
        <BacktestModal
          algorithm={algorithm}
          onClose={() => setShowBacktestModal(false)}
        />
      )}
    </div>
  )
}